<template>
    <validation-observer
        ref="add_edit_cancel_policy_form"
        v-slot="{ meta, handleSubmit, errors, setFieldValue, handleReset, validate }"
    >
        <modal
            :title="title"
            :id="modalId"
            size="lg"
            ok-title="Save"
            :ok-disabled="!meta.valid"
            @hidden="onHideModal(handleReset)"
            @ok.prevent="handleSubmit(_, submitForm)"
            :hide-backdrop="true"
            @shown="setData"
        >
            <busy-modal :show="loading || modalBusy"></busy-modal>
            <b-form v-if="values">
                <validation-provider name="name" rules="required" v-slot="{ handleChange }">
                    <form-input label="Name" v-model="values.name" :error="errors.name" @input="handleChange" />
                </validation-provider>
                <div class="d-flex justify-content-between pt-3 mb-2">
                    <span>Description ({{ selectedLanguageLabel }}) </span>
                    <div class="d-flex justify-content-end flex-wrap">
                        <img
                            v-for="l in languageOptions"
                            :key="l.code"
                            class="select-language-icon"
                            :src="`/static/common/images/flags/${l.code}.png`"
                            @click="setLanguage(l.code, values)"
                        />
                    </div>
                </div>
                <validation-provider
                    :name="isEnglish ? 'description' : 'languages.' + selectedLanguage + '.description'"
                    v-slot="{ errors, field }"
                    :rules="isEnglish ? 'required': ''"
                    immediate
                >
                    <div class="mb-2">
                        <form-textarea 
                            class="mb-1" 
                            :value="isEnglish ? values.description : values.languages[selectedLanguage].description"
                            @input="newValue => updateDescription(newValue)"
                            :error="errors[0]" 
                            rows="6" 
                        />
                        <div class="d-flex justify-content-between mb-1">
                            <span></span>
                            <b-button 
                                v-if="!isEnglish" 
                                variant="default" 
                                size="sm" 
                                @click="autoTranslate(values.description)"
                            >
                                Auto Translate
                            </b-button >
                            <b-button 
                                v-else
                                variant="default" 
                                size="sm" 
                                @click="generateDescription(values)"
                            >
                                Generate Description
                            </b-button >
                        </div>
                    </div>
                </validation-provider>
                <validation-provider name="cancellation_fee" rules="required|validFloat" v-slot="{ handleChange }">
                    <form-input
                        label="Cancellation Fee"
                        v-model="values.cancellation_fee"
                        :error="errors.cancellation_fee"
                        class="mb-0"
                        @input="handleChange"
                    />
                    <small class="text-danger"
                        >Guest has to pay this amount no matter when the booking is cancelled.</small
                    >
                </validation-provider>
                <field-array name="periods" v-slot="{ fields, push, remove }">
                    <div class="d-flex flex-column mt-3">
                        <span class="mb-2">Refunds: </span>
                        <div>
                            <div
                                class="d-flex periods-item flex-md-row flex-column justify-content-md-between justify-content-center align-items-md-baseline align-items-center mb-2 w-100"
                                v-for="(period, i) in fields"
                                :key="period.value.id"
                            >
                                <validation-provider
                                    :name="'periods' + `[${i}].` + 'refund_percent'"
                                    rules="required|validPositiveFloat"
                                    class="period"
                                >
                                    <form-input
                                        class="mb-0 mr-md-2 mr-0 period"
                                        :rightIcon="['fas', 'percent']"
                                        :value="period.value.refund_percent"
                                        @input="(val) => setRefundPercent(i, val)"
                                        :error="errors['periods' + `[${i}].` + 'refund_percent']"
                                    />
                                </validation-provider>
                                <span class="cancel-policies-text text-center">if</span>
                                <validation-provider
                                    :name="'periods' + `[${i}].` + 'nights_before_checkin'"
                                    rules="required"
                                    class="period"
                                >
                                    <form-select
                                        class="mb-0 mb-0 mr-md-2 mr-0 period"
                                        :options="nightsOptions"
                                        :value="period.value.nights_before_checkin"
                                        @input="(val) => setNightsBeforeCheckin(i, val)"
                                        :error="errors['periods' + `[${i}].` + 'nights_before_checkin']"
                                    />
                                </validation-provider>
                                <span class="cancel-policies-text text-center">nights before arrival</span>
                                <b-button
                                    variant="default"
                                    @click="
                                        () => {
                                            remove(i);
                                            removeRefundPeriod(i)
                                            $nextTick(() => validate());
                                        }
                                    "
                                >
                                    <font-awesome-icon :icon="['fas', 'trash']"
                                /></b-button>
                                <hr class="d-md-none d-block w-100" />
                            </div>
                        </div>
                        <div class="d-block d-md-none">
                            <div class="d-flex flex-column mb-2 w-100" v-for="(period, i) in fields" :key="period.id">
                                <validation-provider
                                    :name="'periods' + `[${i}].` + 'refund_percent'"
                                    rules="required|validPositiveFloat"
                                    class="w-100"
                                >
                                    <form-input
                                        class="mb-0"
                                        :rightIcon="['fas', 'percent']"
                                        :value="period.value.refund_percent"
                                        @input="(val) => setRefundPercent(i, val)"
                                        :error="errors['periods' + `[${i}].` + 'refund_percent']"
                                    />
                                </validation-provider>
                                <span class="cancel-policies-text">if</span>
                                <validation-provider
                                    :name="'periods' + `[${i}].` + 'nights_before_checkin'"
                                    rules="required"
                                    class="w-100"
                                >
                                    <form-select
                                        class="mb-0"
                                        :options="nightsOptions"
                                        :value="period.value.nights_before_checkin"
                                        @input="(val) => setNightsBeforeCheckin(i, val)"
                                        :error="errors['periods' + `[${i}].` + 'nights_before_checkin']"
                                    />
                                </validation-provider>
                                <span class="cancel-policies-text">nights before arrival</span>

                                <b-button
                                    variant="default"
                                    @click="
                                        () => {
                                            remove(i);
                                            removeRefundPeriod(i)
                                            $nextTick(() => validate());
                                        }
                                    "
                                >
                                    <font-awesome-icon :icon="['fas', 'trash']"
                                /></b-button>
                                <hr class="w-100" />
                            </div>
                        </div>
                        <b-button
                            @click="addNewRefundPeriod()"
                            variant="default"
                            class="mt-2"
                        >
                            <font-awesome-icon :icon="['fas', 'plus']"
                        /></b-button>
                    </div>
                </field-array>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
import API from '../../../api';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import Modal from '../../generic/modal/modal.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import { FieldArray } from 'vee-validate';
import { toAmountDisplay } from '../../../utils/numbers';
import { formatResponseError } from '../../../utils/api';
import {
    CANCELLATION_POLICY_ACTIVE_LANGUAGES_ENDPOINT,
    CANCELLATION_POLICY_TEMPLATE_AUTO_TRANSLATE_ENDPOINT,
    CANCELLATION_POLICY_TEMPLATE_GENERATE_DESCRIPTION_ENDPOINT,
} from '../../../endpoints';

const defaultPeriod = {
    id: 1,
    nights_before_checkin: 1,
    refund_percent: 1,
};

const defaultValues = {
    name: '',
    description: '',
    cancellation_fee: 0,
    periods: [defaultPeriod],
};

export default {
    components: {
        Modal,
        BusyModal,
        FormInput,
        FormTextarea,
        FormSelect,
        ResponsiveButton,
        FieldArray,
    },
    props: {
        modalId: String,
        policy: Object,
        clone: Boolean,
        modalBusy: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            languageOptions: [],
            selectedLanguage: null,
            nightsOptions: [
                ...Array.from({ length: 90 }, (_, i) => i + 1).map((item) => ({ value: item, text: item })),
                { value: 120, text: 120 },
                { value: 150, text: 150 },
                { value: 180, text: 180 },
            ],
            values: {
                ...defaultValues,
            },
        };
    },
    mounted() {
        this.fetchActiveLanguages();
    },
    computed: {
        title() {
            return this.policy
                ? `${this.clone ? 'Clone': 'Edit '} Cancellation Policy`
                : 'Add Cancellation Policy';
        },
        selectedLanguageLabel() {
            return this.languageOptions.find((item) => item.code === this.selectedLanguage)?.name || '';
        },
        isEnglish() {
            return this.selectedLanguage === 'en';
        },
    },
    methods: {
        submitForm(values) {
            if (this.clone) this.$emit('submit', values, true);
            else this.$emit('submit', values, this.policy?.id);
        },
        onHideModal(handleReset) {
            handleReset()
            this.$emit('hidden');
        },
        getInitialValues() {
            const policy = this.policy;
            const languagesObject = this.languageOptions
                .filter((lang) => lang.code !== 'en')
                .reduce((acc, lang) => {
                    acc[lang.code] = {
                        id: lang.id,
                        code: lang.code,
                        name: '',
                        description: '',
                    };
                    return acc;
                }, {});
            return policy ? JSON.parse(JSON.stringify(policy)) : { ...defaultValues, languages: languagesObject };
        },
        setData() {
            if (this.clone && !this.policy.name.endsWith(' Clone')) this.policy.name += ' Clone';
            this.values = this.getInitialValues();
            this.$refs.add_edit_cancel_policy_form.setValues({
                ...this.values,
            });
        },
        setErrors(errors) {
            this.$refs.add_edit_cancel_policy_form.setErrors(errors);
        },
        setLanguage(language, values) {
            this.selectedLanguage = language;
        },
        async fetchActiveLanguages() {
            try {
                this.loading = true;
                const { data } = await API.get(CANCELLATION_POLICY_ACTIVE_LANGUAGES_ENDPOINT());
                this.languageOptions = [{ id: null, code: 'en', name: 'English' }, ...data];
                this.selectedLanguage = this.languageOptions[0].code;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch active languages' });
            } finally {
                this.loading = false;
            }
        },
        addNewRefundPeriod() {
            const newPeriod = { refund_percent: 1, nights_before_checkin: 1, id: Date.now() };
            const currentPeriods = this.values.periods || [];
            currentPeriods.push(newPeriod);
            this.$refs.add_edit_cancel_policy_form.setFieldValue('periods', currentPeriods);
        },
        removeRefundPeriod(index) {
            const currentPeriods = this.values.periods || [];
            currentPeriods.splice(index, 1);
            this.$refs.add_edit_cancel_policy_form.setFieldValue('periods', currentPeriods);
        },
        setRefundPercent(index, value) {
            this.values.periods[index].refund_percent = value;
            this.$refs.add_edit_cancel_policy_form.setFieldValue(`periods[${index}].refund_percent`, value);
        },
        setNightsBeforeCheckin(index, value) {
            this.values.periods[index].nights_before_checkin = value;
            this.$refs.add_edit_cancel_policy_form.setFieldValue(`periods[${index}].nights_before_checkin`, value);
        },
        updateDescription(newValue) {
            if (this.isEnglish) {
                this.values.description = newValue;
                this.$refs.add_edit_cancel_policy_form.setFieldValue('description', newValue);
            } else {
                if (!this.values.languages[this.selectedLanguage]) {
                    this.$set(this.values.languages, this.selectedLanguage, { description: '' });
                }
                this.values.languages[this.selectedLanguage].description = newValue;
                this.$refs.add_edit_cancel_policy_form.setFieldValue(
                    `languages.${this.selectedLanguage}.description`,
                    newValue
                );
            }
        },
        async generateDescription(values) {
            try {
                this.loading = true;
                const { data } = await API.post(CANCELLATION_POLICY_TEMPLATE_GENERATE_DESCRIPTION_ENDPOINT(), values);
                this.values.description = data;
                this.$refs.add_edit_cancel_policy_form.setFieldValue('description', data);
                this.successToast({ text: 'Description successfully generated.' });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not generate description.' });
            } finally {
                this.loading = false;
            }
        },
        async autoTranslate(text) {
            try {
                this.loading = true;
                const { data } = await API.post(CANCELLATION_POLICY_TEMPLATE_AUTO_TRANSLATE_ENDPOINT(), {
                    code: this.selectedLanguage,
                    text: text,
                });
                this.values.description = text;
                this.values.languages[this.selectedLanguage].description = data;
                this.$refs.add_edit_cancel_policy_form.setFieldValue(`languages.${this.selectedLanguage}.description`, data);
                this.successToast({ text: 'Description successfully translated.' });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not translate description.' });
            } finally {
                this.loading = false;
            }
        },
    },
    watch: {
        policy() {
            this.values = this.getInitialValues();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';
.periods-item {
    gap: 0.5rem;
    .period {
        width: 25%;
    }
}

@include media-breakpoint-down(md) {
    .cancel-policies-text {
        text-align: center;
        display: block;
        margin: 10px 0;
    }
    .periods-item .period {
        width: 100%;
    }
}

.select-language-icon {
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
    cursor: pointer;
}
</style>
